import React, { useState, useCallback } from 'react';
import Button from '../../../composants/Button/Button';
import SectionPliable from '../../../composants/SectionPliable/SectionPliable';
import { formatDateHeure } from '../domain/analyse/dates';
import { OperationMS } from '../types';
import { CommandeAnalyse } from '../domain/analyse/types';
import { getNotificationDate, getSignatureAcheteurInfo } from '../domain/analyse/historique';
import { sendNotification } from '../services/operationMarcheService';

// Section « Information aux candidats » :
//  - RETENU : notifie AUTOMATIQUEMENT a la signature (date = evenement « Signature acheteur »).
//  - NON-RETENUS : notification MANUELLE par bouton « Notifier le candidat »
//    (POST /subsequents/notifications/send) ; date = evenement « Notification » (historique).
interface OperationMarcheCandidatsProps {
  operation: OperationMS;
  retenu?: CommandeAnalyse;
  nonRetenus: CommandeAnalyse[];
  onSeeMessage: (commande: CommandeAnalyse) => void;
  onNotified: (commandeId: number) => void;
  // Date d'envoi optimiste (commandeId -> ISO) avant le retour du refetch backend.
  justNotified: Record<string, string>;
}

const candidatNom = (c?: CommandeAnalyse): string => c?.contrat?.entite?.nom || 'Nom inconnu';
const candidatMail = (c?: CommandeAnalyse): string =>
  c?.contrat?.entite?.interlocuteur?.email_contact || '—';

const OperationMarcheCandidats: React.FC<OperationMarcheCandidatsProps> = ({
  operation,
  retenu,
  nonRetenus,
  onSeeMessage,
  onNotified,
  justNotified,
}) => {
  const [notifyingId, setNotifyingId] = useState<number | null>(null);
  const [errorId, setErrorId] = useState<number | null>(null);

  const selectedCommandeId = operation.id_commande;
  // Date de notification du retenu = date de signature acheteur (notif auto a la signature).
  const signatureDate = formatDateHeure(getSignatureAcheteurInfo(operation.historique).date);

  const notifier = useCallback(
    async (commande: CommandeAnalyse) => {
      if (commande.id === undefined || selectedCommandeId === undefined) return;
      setNotifyingId(commande.id);
      setErrorId(null);
      try {
        await sendNotification({
          operation_id: operation.id,
          commande_id: commande.id,
          selected_commande_id: selectedCommandeId,
        });
        onNotified(commande.id);
      } catch {
        setErrorId(commande.id);
      } finally {
        setNotifyingId(null);
      }
    },
    [operation.id, selectedCommandeId, onNotified],
  );

  if (!retenu && nonRetenus.length === 0) return null;

  return (
    <SectionPliable title="Information aux candidats" defaultOpen>
      <div className="opms__marche-body">
        {retenu && (
          <div className="opms__candidats">
            <div className="opms__candidats-row opms__candidats-head">
              <span>Retenu</span>
              <span>Mail</span>
              <span>Contenu de la notification</span>
              <span>Notification</span>
            </div>
            <div className="opms__candidats-row">
              <span className="opms__candidats-nom">{candidatNom(retenu)}</span>
              <span className="opms__candidats-mail">{candidatMail(retenu)}</span>
              <span>
                <button type="button" className="opms__candidats-link" onClick={() => onSeeMessage(retenu)}>
                  Voir le message
                </button>
              </span>
              <span>{signatureDate ? `Notifié le ${signatureDate}` : '—'}</span>
            </div>
          </div>
        )}

        {nonRetenus.length > 0 && (
          <div className="opms__candidats">
            <div className="opms__candidats-row opms__candidats-head">
              <span>Non retenus</span>
              <span>Mail</span>
              <span>Contenu du message de rejet</span>
              <span>Information de rejet</span>
            </div>
            {nonRetenus.map((commande) => {
              const date = formatDateHeure(
                getNotificationDate(operation.historique, commande.id) ??
                  justNotified[String(commande.id)] ??
                  '',
              );
              return (
                <div className="opms__candidats-row" key={commande.id}>
                  <span className="opms__candidats-nom">{candidatNom(commande)}</span>
                  <span className="opms__candidats-mail">{candidatMail(commande)}</span>
                  <span>
                    <button
                      type="button"
                      className="opms__candidats-link"
                      onClick={() => onSeeMessage(commande)}
                    >
                      Voir le message
                    </button>
                  </span>
                  <span>
                    {date ? (
                      `Diffusé le ${date}`
                    ) : (
                      <span className="opms__candidats-action">
                        <Button
                          variant="primary"
                          value={notifyingId === commande.id ? 'Envoi...' : 'Informer le candidat'}
                          onClick={() => notifier(commande)}
                          disabled={notifyingId !== null || selectedCommandeId === undefined}
                        />
                        {errorId === commande.id && (
                          <span className="opms__candidats-error">Échec de l'envoi, réessayez.</span>
                        )}
                      </span>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </SectionPliable>
  );
};

export default OperationMarcheCandidats;
